/**
 * Audio engine.
 *
 * The hero feature is `swapMask()`: a single shadowy figure changes voice
 * mid-thought. The illusion is sold by the timing — a held breath, the dry
 * click of paper-on-wood, then the next voice already mid-sentence.
 */

export type VoiceKey = string;
export type SfxKey = string;

type Bus = {
  input: GainNode;   // pre-duck gain (volume control)
  output: GainNode;  // post-duck gain (ducked when voice plays)
};

const VOICE_DUCK = 0.25;
const DUCK_RAMP_DOWN = 0.15;
const DUCK_RAMP_UP = 0.6;

export class AudioEngine {
  private ctx: AudioContext | null = null;
  private master!: GainNode;
  private voiceBus!: Bus;
  private sfxBus!: Bus;
  private musicBus!: Bus;
  private ambientBus!: Bus;
  private reverb!: ConvolverNode;
  private reverbSend!: GainNode;

  private buffers = new Map<string, AudioBuffer>();
  private ambientSource: AudioBufferSourceNode | null = null;
  private musicSource: AudioBufferSourceNode | null = null;
  private musicGain: GainNode | null = null;
  private musicKey: string | null = null;

  private duckActive = 0; // refcount of voice lines currently playing

  /** Microphone capture for the voice-input mechanic. Initialized lazily via
   *  requestMicrophone(); never connected to output (no feedback). */
  private micStream: MediaStream | null = null;
  private micAnalyser: AnalyserNode | null = null;
  private micTimeData: Uint8Array<ArrayBuffer> | null = null;
  private micPermissionGranted = false;
  private micRecorder: MediaRecorder | null = null;
  private micChunks: Blob[] = [];

  /** Initialize the AudioContext. Must be called on a user gesture. */
  async init() {
    if (this.ctx) return;
    this.ctx = new AudioContext();

    this.master = this.ctx.createGain();
    this.master.gain.value = 1.0;
    this.master.connect(this.ctx.destination);

    this.voiceBus = this.makeBus(0.95);
    this.sfxBus = this.makeBus(0.8);
    this.musicBus = this.makeBus(0.5);
    this.ambientBus = this.makeBus(0.4);

    // Synthetic stage reverb — dry-ish wood-and-curtains room.
    this.reverb = this.ctx.createConvolver();
    this.reverb.buffer = this.makeImpulse(1.4, 2.2);
    this.reverbSend = this.ctx.createGain();
    this.reverbSend.gain.value = 0.18;
    this.voiceBus.output.connect(this.reverbSend);
    this.reverbSend.connect(this.reverb);
    this.reverb.connect(this.master);
  }

  /**
   * One-time permission check for the microphone. Opens then immediately
   * closes the stream so the OS/browser caches the grant. Throws on denial.
   * The mic is NOT held open after this returns — call openMic() each time
   * you actually want to listen (e.g. while V is held).
   */
  async requestMicrophone(): Promise<void> {
    if (this.micPermissionGranted) return;
    await this.openMic();
    this.closeMic();
    this.micPermissionGranted = true;
  }

  /** Open the mic stream and wire it to an AnalyserNode (for live amplitude
   *  reads) AND a MediaRecorder (so closeMic() can return the captured audio
   *  blob for upstream STT). Idempotent — no-op if already open. */
  async openMic(): Promise<void> {
    if (!this.ctx) await this.init();
    if (this.micStream) return;
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    this.micStream = stream;
    const source = this.ctx!.createMediaStreamSource(stream);
    const analyser = this.ctx!.createAnalyser();
    analyser.fftSize = 1024;
    analyser.smoothingTimeConstant = 0.7;
    source.connect(analyser);
    this.micAnalyser = analyser;
    this.micTimeData = new Uint8Array(new ArrayBuffer(analyser.fftSize));

    // Recorder captures the same stream so STT can transcribe it later.
    this.micChunks = [];
    let mr: MediaRecorder;
    try {
      mr = new MediaRecorder(stream, { mimeType: 'audio/webm' });
    } catch {
      mr = new MediaRecorder(stream);
    }
    mr.ondataavailable = e => {
      if (e.data && e.data.size > 0) this.micChunks.push(e.data);
    };
    mr.start();
    this.micRecorder = mr;
  }

  /** Close the mic stream and tear down everything. Returns the blob captured
   *  during this open/close cycle (suitable for STT upload), or null if no
   *  audio was recorded. Stops all tracks so the OS mic indicator goes off. */
  async closeMic(): Promise<Blob | null> {
    let blob: Blob | null = null;
    const mr = this.micRecorder;
    if (mr && mr.state !== 'inactive') {
      blob = await new Promise<Blob>(resolve => {
        mr.onstop = () => {
          const mime = mr.mimeType || 'audio/webm';
          resolve(new Blob(this.micChunks, { type: mime }));
        };
        try { mr.stop(); } catch { resolve(new Blob([])); }
      });
    }
    this.micRecorder = null;
    this.micChunks = [];
    if (this.micStream) {
      for (const track of this.micStream.getTracks()) track.stop();
      this.micStream = null;
    }
    if (this.micAnalyser) {
      this.micAnalyser.disconnect();
      this.micAnalyser = null;
    }
    this.micTimeData = null;
    return blob;
  }

  /** Whether the mic is currently open and producing samples. */
  isMicOpen(): boolean {
    return this.micStream !== null && this.micAnalyser !== null;
  }

  /** Live RMS amplitude of the microphone signal in 0..1. Returns 0 if the
   *  mic isn't currently open (i.e. V isn't held). */
  getMicAmplitude(): number {
    if (!this.micAnalyser || !this.micTimeData) return 0;
    this.micAnalyser.getByteTimeDomainData(this.micTimeData);
    let sum = 0;
    for (const v of this.micTimeData) {
      const x = (v - 128) / 128;
      sum += x * x;
    }
    return Math.sqrt(sum / this.micTimeData.length);
  }

  /** Set the music bus output gain (0..1) — used to duck the score under
   *  conversation moments where another audio source is playing the agent. */
  setMusicDuck(level: number) {
    if (!this.ctx) return;
    const t = this.ctx.currentTime;
    this.musicBus.output.gain.cancelScheduledValues(t);
    this.musicBus.output.gain.linearRampToValueAtTime(level, t + 0.6);
  }

  /** Set how reverberant the room sounds. 0 = dry (backstage), 1 = audience hall. */
  setRoomTone(mix: number) {
    if (!this.ctx) return;
    const t = this.ctx.currentTime;
    this.reverbSend.gain.cancelScheduledValues(t);
    this.reverbSend.gain.linearRampToValueAtTime(mix * 0.32, t + 0.6);
  }

  async loadAudio(key: string, url: string) {
    if (!this.ctx) await this.init();
    if (this.buffers.has(key)) return;
    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status} for ${url}`);
      const arr = await res.arrayBuffer();
      const buf = await this.ctx!.decodeAudioData(arr);
      this.buffers.set(key, buf);
    } catch (err) {
      console.warn(`[audio] missing or failed: ${key} (${url})`, err);
    }
  }

  async loadMany(entries: Array<{ key: string; url: string }>) {
    await Promise.all(entries.map(e => this.loadAudio(e.key, e.url)));
  }

  /** Decoded duration in seconds for a previously-loaded voice line, or
   *  null if not loaded. Used by the typewriter subtitle to lock the
   *  reveal length to the actual audio length. */
  getLineDuration(key: VoiceKey): number | null {
    const buf = this.buffers.get(key);
    return buf ? buf.duration : null;
  }

  /** Play a voice line. Resolves when the line ends. Ducks ambient/music. */
  async playLine(key: VoiceKey, opts: { volume?: number } = {}): Promise<void> {
    const buf = this.buffers.get(key);
    if (!buf || !this.ctx) {
      console.warn(`[audio] line missing: ${key}`);
      return;
    }
    this.duckBegin();
    const { source, gain } = this.makeSource(buf, this.voiceBus.input, opts.volume ?? 1);
    return new Promise(resolve => {
      source.onended = () => {
        gain.disconnect();
        this.duckEnd();
        resolve();
      };
      source.start();
    });
  }

  playSfx(key: SfxKey, opts: { volume?: number; pitch?: number } = {}): Promise<void> {
    const buf = this.buffers.get(key);
    if (!buf || !this.ctx) {
      // Silent during the pre-init window (audio context not yet created
      // — the user hasn't clicked anything yet). Only warn once the
      // engine is initialised, since at that point a missing buffer is
      // a real authoring miss worth surfacing.
      if (this.ctx) console.warn(`[audio] sfx missing: ${key}`);
      return Promise.resolve();
    }
    const { source, gain } = this.makeSource(buf, this.sfxBus.input, opts.volume ?? 1);
    if (opts.pitch) source.playbackRate.value = opts.pitch;
    return new Promise(resolve => {
      source.onended = () => { gain.disconnect(); resolve(); };
      source.start();
    });
  }

  startAmbient(key: SfxKey, volume = 1) {
    const buf = this.buffers.get(key);
    if (!buf || !this.ctx) return;
    this.stopAmbient();
    const src = this.ctx.createBufferSource();
    src.buffer = buf;
    src.loop = true;
    const g = this.ctx.createGain();
    g.gain.value = 0;
    src.connect(g).connect(this.ambientBus.input);
    src.start();
    g.gain.linearRampToValueAtTime(volume, this.ctx.currentTime + 1.5);
    this.ambientSource = src;
  }

  /** Play a long-form looping music track on the music bus. Cross-fades from
   *  any currently-playing track (1.5s in, 1.5s out). No-op if `key` is the
   *  one already playing. */
  startMusic(key: SfxKey, volume = 1) {
    if (!this.ctx) return;
    if (this.musicKey === key && this.musicSource) return;
    const buf = this.buffers.get(key);
    if (!buf) {
      console.warn(`[audio] music missing: ${key}`);
      return;
    }
    this.stopMusic(1500);
    const src = this.ctx.createBufferSource();
    src.buffer = buf;
    src.loop = true;
    const g = this.ctx.createGain();
    g.gain.value = 0;
    src.connect(g).connect(this.musicBus.input);
    src.start();
    g.gain.linearRampToValueAtTime(volume, this.ctx.currentTime + 1.5);
    this.musicSource = src;
    this.musicGain = g;
    this.musicKey = key;
  }

  stopMusic(fadeMs = 1500) {
    if (!this.ctx || !this.musicSource || !this.musicGain) return;
    const src = this.musicSource;
    const g = this.musicGain;
    this.musicSource = null;
    this.musicGain = null;
    this.musicKey = null;
    const t = this.ctx.currentTime;
    g.gain.cancelScheduledValues(t);
    g.gain.linearRampToValueAtTime(0, t + fadeMs / 1000);
    setTimeout(() => { try { src.stop(); g.disconnect(); } catch { /* already stopped */ } }, fadeMs);
  }

  stopAmbient(fadeMs = 600) {
    if (!this.ctx || !this.ambientSource) return;
    const src = this.ambientSource;
    this.ambientSource = null;
    const t = this.ctx.currentTime;
    // We don't have direct access to its gain here; redirect via a ramp on the bus output.
    // Simpler: just stop after fade.
    setTimeout(() => { try { src.stop(); } catch { /* already stopped */ } }, fadeMs);
    void t;
  }

  /**
   * The mask swap. The puppeteer changes voice mid-thought.
   *
   * Sequence:
   *   1. brief held breath (0.3s) — felt, not heard loud
   *   2. dry mask-on-wood click (overlaps with end of breath)
   *   3. next voice begins
   *
   * `onMaskLifted` fires synced with the click — wire this to Stage.liftMask()
   * so the visual lifts as the audio clicks.
   */
  async swapMask(opts: {
    nextLine: VoiceKey;
    breathSfx?: SfxKey;
    clackSfx?: SfxKey;
    onMaskLifted?: () => void;
    /** Fires the moment playLine actually starts (after breath + clack).
     *  Lets the caller sync a typewriter or visual cue to the voice. */
    onLineStart?: () => void;
    /** ms of held silence between breath and clack. Tune for menace. */
    holdMs?: number;
  }): Promise<void> {
    const breath = opts.breathSfx ?? 'breath';
    const clack = opts.clackSfx ?? 'mask_clack';
    const hold = opts.holdMs ?? 220;

    // Breath — quiet, not the focus.
    void this.playSfx(breath, { volume: 0.5 });
    await wait(380);

    // Held silence.
    await wait(hold);

    // Mask clack + visual lift, simultaneous.
    void this.playSfx(clack, { volume: 0.7 });
    opts.onMaskLifted?.();
    await wait(80);

    // Next voice starts before the clack tail dies. The overlap is the trick.
    opts.onLineStart?.();
    await this.playLine(opts.nextLine);
  }

  private makeBus(initialVolume: number): Bus {
    const input = this.ctx!.createGain();
    const output = this.ctx!.createGain();
    input.gain.value = initialVolume;
    output.gain.value = 1;
    input.connect(output);
    output.connect(this.master);
    return { input, output };
  }

  private makeSource(buf: AudioBuffer, dest: AudioNode, volume: number) {
    const ctx = this.ctx!;
    const source = ctx.createBufferSource();
    source.buffer = buf;
    const gain = ctx.createGain();
    gain.gain.value = volume;
    source.connect(gain).connect(dest);
    return { source, gain };
  }

  private duckBegin() {
    this.duckActive++;
    if (!this.ctx) return;
    if (this.duckActive === 1) {
      const t = this.ctx.currentTime;
      this.musicBus.output.gain.cancelScheduledValues(t);
      this.ambientBus.output.gain.cancelScheduledValues(t);
      this.musicBus.output.gain.linearRampToValueAtTime(VOICE_DUCK, t + DUCK_RAMP_DOWN);
      this.ambientBus.output.gain.linearRampToValueAtTime(VOICE_DUCK, t + DUCK_RAMP_DOWN);
    }
  }

  private duckEnd() {
    this.duckActive = Math.max(0, this.duckActive - 1);
    if (!this.ctx) return;
    if (this.duckActive === 0) {
      const t = this.ctx.currentTime;
      this.musicBus.output.gain.cancelScheduledValues(t);
      this.ambientBus.output.gain.cancelScheduledValues(t);
      this.musicBus.output.gain.linearRampToValueAtTime(1, t + DUCK_RAMP_UP);
      this.ambientBus.output.gain.linearRampToValueAtTime(1, t + DUCK_RAMP_UP);
    }
  }

  /** Decaying noise impulse — cheap, plausible room reverb. */
  private makeImpulse(seconds: number, decay: number): AudioBuffer {
    const ctx = this.ctx!;
    const rate = ctx.sampleRate;
    const length = Math.floor(rate * seconds);
    const buf = ctx.createBuffer(2, length, rate);
    for (let ch = 0; ch < 2; ch++) {
      const data = buf.getChannelData(ch);
      for (let i = 0; i < length; i++) {
        data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / length, decay);
      }
    }
    return buf;
  }
}

function wait(ms: number) {
  return new Promise<void>(r => setTimeout(r, ms));
}
